import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { inboundFoundStock } from '../../services/foundStockServices'
import { FoundStockDialogShell } from './FoundStockDialogShell'
import { FrozenProposalEventLink } from './FrozenProposalEventLink'
import { useSettlementRequest } from './useSettlementRequest'

const T = 'found_stock.inbound_dialog'

export const InboundFoundStockDialog = ({
  open,
  warehouseId,
  group,
  product = null,
  onSuccess = () => {},
  onCancel = () => {},
  onGateOff = () => {},
  onStale = () => {},
}) => {
  const { i18n } = useTranslation()
  const { busy, errorMessage, errorCoverageEventId, reset, run } = useSettlementRequest({ onSuccess, onGateOff, onStale })

  useEffect(() => {
    if (!open) reset()
  }, [open, reset])

  const onConfirm = () => {
    if (!group) return
    // The receipt's piece count is whatever the server actually converted
    // (`converted_stock_unit_ids`), not the group's quantity: a concurrent settlement can shrink it.
    run(
      () => inboundFoundStock(warehouseId, group.stock_unit_ids),
      result => {
        if (!result.replayed) {
          return i18n.t(`${T}.success`, { replenishment_id: result.replenishment_id, count: (result.converted_stock_unit_ids || []).length })
        }
        // A replay payload has no receipt fields, only `units[]`. Recovering the receipt id from
        // them tells the operator WHICH receipt already exists — a bare "already settled" would
        // leave them unsure whether the merchant was billed at all.
        const units = result.units || []
        const replenishmentId = units.find(u => u.replenishment_id)?.replenishment_id
        if (replenishmentId == null) return i18n.t(`${T}.replayed`)
        return i18n.t(`${T}.replayed_receipt`, { replenishment_id: replenishmentId, count: units.filter(u => u.outcome === 'converted').length })
      },
    )
  }

  return (
    <FoundStockDialogShell
      open={open}
      title={i18n.t(`${T}.title`)}
      summary={i18n.t(`${T}.summary`, { id: group?.declaration_id, location: group?.location_code, quantity: group?.quantity })}
      product={product}
      productId={group?.product_id}
      busy={busy}
      errorMessage={errorMessage}
      errorAction={<FrozenProposalEventLink warehouseId={warehouseId} eventId={errorCoverageEventId} />}
      confirmLabel={i18n.t(`${T}.confirm`)}
      cancelLabel={i18n.t(`${T}.cancel`)}
      onConfirm={onConfirm}
      onCancel={onCancel}
    >
      <p className="text-sm text-amber-700 bg-amber-50 rounded p-3">{i18n.t(`${T}.consequence`)}</p>
      <p className="text-xs text-gray-500 mt-2">{i18n.t(`${T}.found_bay_note`)}</p>
    </FoundStockDialogShell>
  )
}
